import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getOffers, createOffer, updateOffer, deleteOffer, getCustomers, getProspects, getRepresentativeProducts, getUsers } from '@/api/api';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { FileText, Plus, Edit, Trash2, Eye, Download, Calendar, User, Package, Search, FileSpreadsheet } from 'lucide-react';
import { format } from 'date-fns';
import * as XLSX from 'xlsx';
import { tr } from 'date-fns/locale';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import PageHeader from '@/components/PageHeader';
import { getLocalDateString } from '@/utils/dateUtils';

const Offers = ({ user }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [offers, setOffers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [prospects, setProspects] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [editingOffer, setEditingOffer] = useState(null);
  const [previewOffer, setPreviewOffer] = useState(null);
  const [customerSearch, setCustomerSearch] = useState('');
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [showProductDropdown, setShowProductDropdown] = useState(null); // index of item
  
  // Tarih filtreleri
  const today = new Date();
  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const [startDate, setStartDate] = useState(format(firstDayOfMonth, 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(format(today, 'yyyy-MM-dd'));
  const [filteredOffers, setFilteredOffers] = useState([]);
  const [searchQuery, setSearchQuery] = useState(''); // Arama filtresi
  const [representatives, setRepresentatives] = useState([]); // Temsilci listesi
  const [selectedRepresentative, setSelectedRepresentative] = useState('all'); // Seçili temsilci
  
  const [formData, setFormData] = useState({
    customer_id: '',
    offer_type: 'Peşin',
    title: '',
    items: [{ product_name: '', quantity: 1, unit_price: '', discount_percentage: 0, discounted_price: '', total: 0 }],
    notes: '',
    valid_until: '',
    include_vat: true
  });

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    // Müşteri veya müşteri adayı detay sayfasından gelindiyse otomatik formu aç
    if (location.state?.customerId && (customers.length > 0 || prospects.length > 0)) {
      // Önce müşterilerde ara, sonra müşteri adaylarında
      const customer = customers.find(c => c.id === location.state.customerId) ||
                      prospects.find(p => p.id === location.state.customerId);
      
      setFormData({
        ...formData,
        customer_id: location.state.customerId,
        title: 'Arvento Araç Takip Sistemleri Fiyat Teklifi'
      });
      if (customer) {
        setCustomerSearch(customer.name);
        setShowCustomerDropdown(false);
      }
      setShowDialog(true);
      // State'i temizle
      navigate(location.pathname, { replace: true });
    }
  }, [customers, prospects, location.state]);

  // Dışarı tıklandığında dropdown'u kapat
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showCustomerDropdown && !event.target.closest('.customer-search-container')) {
        setShowCustomerDropdown(false);
      }
      if (showProductDropdown !== null && !event.target.closest('.relative')) {
        setShowProductDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showCustomerDropdown, showProductDropdown]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [offersData, customersData, prospectsData, productsData, usersData] = await Promise.all([
        getOffers(),
        getCustomers(),
        getProspects(),
        getRepresentativeProducts(),
        getUsers()
      ]);
      setOffers(offersData);
      setCustomers(customersData);
      setProspects(prospectsData);
      setProducts(productsData);
      // Temsilcileri filtrele
      const reps = usersData.filter(u => u.role === 'representative' || u.role === 'temsilci');
      setRepresentatives(reps);
      filterOffers(offersData);
    } catch (error) {
      toast.error('Veriler yüklenirken hata oluştu');
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const filterOffers = (offersToFilter = offers) => {
    let filtered = offersToFilter;
    
    // Tarih filtresi (yerel/TR saatine göre)
    if (startDate && endDate) {
      filtered = filtered.filter(offer => {
        const offerDate = getLocalDateString(new Date(offer.created_at));
        return offerDate >= startDate && offerDate <= endDate;
      });
    }
    
    // Arama filtresi (müşteri adı)
    if (searchQuery.trim()) {
      const query = searchQuery.toLocaleLowerCase('tr-TR');
      filtered = filtered.filter(offer => 
        offer.customer_name?.toLocaleLowerCase('tr-TR').includes(query) ||
        offer.offer_number?.toLocaleLowerCase('tr-TR').includes(query) ||
        offer.title?.toLocaleLowerCase('tr-TR').includes(query)
      );
    }
    
    // Temsilci filtresi
    if (selectedRepresentative !== 'all') {
      filtered = filtered.filter(offer => offer.representative === selectedRepresentative);
    }
    
    // En yeni kayıt en üstte olacak şekilde sırala
    filtered = filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    
    setFilteredOffers(filtered);
  };
  
  useEffect(() => {
    filterOffers();
  }, [startDate, endDate, offers, searchQuery, selectedRepresentative]);

  const handleAddItem = () => {
    if (formData.offer_type === 'Peşin') {
      setFormData({
        ...formData,
        items: [...formData.items, { product_name: '', quantity: 1, unit_price: '', discount_percentage: 0, discounted_price: '', total: 0 }]
      });
    } else {
      // Taksitli için
      setFormData({
        ...formData,
        items: [...formData.items, { product_name: '', quantity: 1, months: 12, monthly_payment: '', total: 0 }]
      });
    }
  };

  const handleRemoveItem = (index) => {
    const newItems = formData.items.filter((_, i) => i !== index);
    setFormData({ ...formData, items: newItems });
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index][field] = value;
    
    // Peşin için toplam hesapla
    if (formData.offer_type === 'Peşin' && (field === 'quantity' || field === 'unit_price' || field === 'discounted_price')) {
      const qty = parseFloat(newItems[index].quantity) || 0;
      const unitPrice = parseFloat(newItems[index].unit_price) || 0;
      const discountedPrice = parseFloat(newItems[index].discounted_price) || 0;
      
      // Eğer indirimli fiyat varsa onu kullan, yoksa birim fiyatı kullan
      const finalPrice = discountedPrice > 0 ? discountedPrice : unitPrice;
      newItems[index].total = qty * finalPrice;
    }
    
    // Taksitli için toplam hesapla (quantity x ay x aylık taksit)
    if ((formData.offer_type === 'Arvento Taksitli' || formData.offer_type === 'Operatör Taksitli') && 
        (field === 'quantity' || field === 'months' || field === 'monthly_payment')) {
      const qty = newItems[index].quantity === '' ? 1 : parseFloat(newItems[index].quantity) || 1;
      const months = newItems[index].months || 12;
      const monthlyPayment = newItems[index].monthly_payment === '' ? 0 : parseFloat(newItems[index].monthly_payment) || 0;
      newItems[index].total = qty * months * monthlyPayment;
    }
    
    setFormData({ ...formData, items: newItems });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.customer_id || !formData.title || formData.items.length === 0) {
      toast.error('Lütfen tüm zorunlu alanları doldurun');
      return;
    }

    try {
      // Items'ı temizle - teklif türüne göre sadece gerekli alanları gönder
      const cleanedItems = formData.items.map(item => {
        if (formData.offer_type === 'Peşin') {
          return {
            product_name: item.product_name,
            quantity: parseFloat(item.quantity) || 1,
            unit_price: parseFloat(item.unit_price) || 0,
            discount_percentage: parseFloat(item.discount_percentage) || 0,
            discounted_price: parseFloat(item.discounted_price) || parseFloat(item.unit_price) || 0,
            total: item.total
          };
        } else {
          // Taksitli
          return {
            product_name: item.product_name,
            quantity: parseFloat(item.quantity) || 1,
            months: item.months || 12,
            monthly_payment: parseFloat(item.monthly_payment) || 0,
            total: item.total
          };
        }
      });
      
      const offerData = {
        ...formData,
        items: cleanedItems
      };
      
      if (editingOffer) {
        await updateOffer(editingOffer.id, offerData);
        toast.success('Teklif başarıyla güncellendi');
      } else {
        await createOffer(offerData);
        toast.success('Teklif başarıyla oluşturuldu');
      }
      
      setShowDialog(false);
      setEditingOffer(null);
      resetForm();
      loadData();
    } catch (error) {
      toast.error('Teklif kaydedilirken hata oluştu');
      console.error('Error saving offer:', error);
    }
  };

  const handleEdit = (offer) => {
    setEditingOffer(offer);
    setFormData({
      customer_id: offer.customer_id,
      offer_type: offer.offer_type || 'Peşin',
      title: offer.title,
      items: offer.items,
      notes: offer.notes || '',
      valid_until: offer.valid_until.split('T')[0]
    });
    setCustomerSearch(offer.customer_name);
    setShowCustomerDropdown(false);
    setShowDialog(true);
  };

  const handleDelete = async (offerId) => {
    if (!window.confirm('Bu teklifi silmek istediğinizden emin misiniz?')) return;
    
    try {
      await deleteOffer(offerId);
      toast.success('Teklif başarıyla silindi');
      loadData();
    } catch (error) {
      toast.error('Teklif silinirken hata oluştu');
      console.error('Error deleting offer:', error);
    }
  };

  const handlePreview = (offer) => {
    setPreviewOffer(offer);
    setShowPreview(true);
  };

  const handleDownloadPDF = async (offer) => {
    try {
      // Dosya adı: ProgoArvento-MüşteriAdı-TeklifNo
      const customerName = offer.customer_name.replace(/[^a-zA-Z0-9ğüşıöçĞÜŞİÖÇ\s]/g, '').trim();
      const filename = `ProgoArvento-${customerName}-${offer.offer_number}.pdf`;
      
      // HTML içeriğini oluştur
      const htmlContent = generateOfferHTML(offer);
      
      // Geçici bir div oluştur - EKRAN DIŞINDA
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = htmlContent;
      tempDiv.style.position = 'absolute';
      tempDiv.style.left = '-9999px';
      tempDiv.style.top = '0';
      tempDiv.style.width = '794px';
      tempDiv.style.height = '1123px';
      tempDiv.style.background = 'white';
      tempDiv.style.overflow = 'hidden';
      document.body.appendChild(tempDiv);
      
      await new Promise(resolve => setTimeout(resolve, 800));
      
      // HTML'i canvas'a çevir - TAM A4 BOYUTU
      const canvas = await html2canvas(tempDiv, {
        scale: 1.2,
        useCORS: false,
        allowTaint: true,
        backgroundColor: '#ffffff',
        logging: false,
        width: 794,
        height: 1123,
        windowWidth: 794,
        windowHeight: 1123,
        imageTimeout: 0,
        removeContainer: true
      });
      
      // Canvas'tan image data al - kalite azaltıldı (0.85)
      const imgData = canvas.toDataURL('image/jpeg', 0.85);
      
      // PDF oluştur - A4 boyutu
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
        compress: true
      });
      
      // A4 boyutuna tam sığdır (210x297mm) - FAST compression
      pdf.addImage(imgData, 'JPEG', 0, 0, 210, 297, undefined, 'FAST');
      
      // PDF'i indir
      pdf.save(filename);
      
      // Temizlik
      document.body.removeChild(tempDiv);
      
      // Dialog'u kapat
      setShowPreview(false);
      toast.success('PDF başarıyla indirildi');
      
    } catch (err) {
      console.error('PDF oluşturma hatası:', err);
      toast.error('PDF oluşturulurken hata oluştu: ' + err.message);
    }
  };

  const generateOfferHTML = (offer) => {
    const isInstallment = offer.offer_type === 'Arvento Taksitli' || offer.offer_type === 'Operatör Taksitli';
    const hasDiscountedPrice = !isInstallment && offer.items.some(item => {
      const discountedPrice = parseFloat(item.discounted_price);
      const unitPrice = parseFloat(item.unit_price);
      return !isNaN(discountedPrice) && discountedPrice > 0 && discountedPrice !== unitPrice;
    });
    
    return `
      <!DOCTYPE html>
      <html lang="tr">
      <head>
        <meta charset="utf-8">
        <title>Teklif - ${offer.offer_number}</title>
        <style>
          @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap');
          
          * { 
            margin: 0; 
            padding: 0; 
            box-sizing: border-box; 
          }
          
          body { 
            font-family: 'Inter', 'Segoe UI', system-ui, sans-serif;
            margin: 0;
            padding: 0;
            background: white;
            -webkit-font-smoothing: antialiased;
          }
          
          .container {
            width: 794px;
            height: calc(1123px - 20mm);
            margin: 0 auto;
            padding: 0;
            background: white;
            position: relative;
            page-break-after: always;
          }
          
          @page {
            margin: 0;
          }
          
          @media print {
            * {
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
          }
          
          .header {
            background: #2563EB;
            padding: 30px 40px;
            color: white;
            display: flex;
            align-items: center;
            justify-content: space-between;
            margin: 10mm 10mm 0 10mm;
          }
          
          .header-logo { 
            width: 180px;
            height: auto;
          }
          
          .company-info { 
            font-size: 11px; 
            color: white;
            line-height: 1.8;
            font-weight: 400;
            text-align: right;
            opacity: 0.95;
          }
          .content {
            padding: 20px 0 50px 0;
            margin: 0 10mm;
          }
          
          .offer-title {
            font-size: 28px;
            font-weight: 700;
            color: #1e293b;
            margin-bottom: 10px;
            letter-spacing: -0.5px;
          }
          
          .offer-subtitle {
            font-size: 14px;
            color: #64748b;
            font-weight: 500;
            margin-bottom: 30px;
          }
          table { 
            width: 100%;
            border-collapse: collapse;
            margin: 30px 0;
          }
          
          th { 
            background: white;
            color: #475569; 
            padding: 12px 0; 
            text-align: left;
            font-size: 12px;
            letter-spacing: 0.5px;
            font-weight: 600;
            border-bottom: 1px solid #e2e8f0;
            text-transform: uppercase;
          }
          
          th:not(:first-child), td:not(:first-child) {
            padding-left: 12px;
          }
          
          td { 
            padding: 10px 0; 
            border-bottom: none;
            background: white;
            font-size: 13px;
            color: #334155;
          }
          
          .summary-box {
            background: white;
            padding: 0;
            margin: 20px 0;
            border-top: 1px solid #e2e8f0;
          }
          
          .summary-row {
            display: flex;
            justify-content: space-between;
            padding: 8px 0;
            font-size: 14px;
            color: #475569;
            border-bottom: 1px solid #f1f5f9;
          }
          
          .summary-row:last-child { 
            border-bottom: 2px solid #e2e8f0;
          }
          
          .total-row { 
            font-size: 18px !important;
            font-weight: 700 !important;
            color: #1e293b !important;
          }
          
          .footer { 
            position: absolute;
            bottom: 0;
            left: 10mm;
            right: 10mm;
            background: linear-gradient(135deg, #2563eb 0%, #1e40af 100%);
            padding: 18px 40px;
            font-size: 13px;
            color: white;
            display: flex;
            align-items: center;
            justify-content: space-between;
            margin-bottom: 10mm;
          }
          
          .footer-logo {
            height: 80px;
            width: auto;
          }
        </style>
      </head>
      <body>
        <div class="container">
          <!-- Header -->
          <div class="header">
            <img src="/progo-logo-white.png" alt="ProGo Mobil Sistemler" class="header-logo">
            <div class="company-info">
              Kazımdirik mah. 364/10 Sok. No:11 D:73<br>
              Bornova / İZMİR<br>
              0 232 435 20 02 · info@progofilo.com.tr
            </div>
          </div>
          
          <!-- Content -->
          <div class="content">
            <!-- Title -->
            <div class="offer-title">Fiyat Teklifi</div>
            <div class="offer-subtitle">Teklif No: ${offer.offer_number}</div>
            
            <!-- Info Grid -->
            <div style="margin-bottom: 30px;">
              <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 20px;">
                <div>
                  <div style="font-size: 11px; color: #64748b; font-weight: 600; margin-bottom: 6px; text-transform: uppercase; letter-spacing: 0.5px;">Müşteri</div>
                  <div style="font-size: 16px; color: #1e293b; font-weight: 600;">${offer.customer_name}</div>
                </div>
                <div style="text-align: right;">
                  <div style="font-size: 11px; color: #64748b; font-weight: 600; margin-bottom: 6px; text-transform: uppercase; letter-spacing: 0.5px;">Tarih</div>
                  <div style="font-size: 16px; color: #1e293b; font-weight: 600;">${offer.created_at ? format(new Date(offer.created_at), 'dd.MM.yyyy') : '-'}</div>
                </div>
              </div>
            </div>
            
            <!-- Products Table -->
            <table>
              <thead>
                <tr>
                  <th>Ürün/Hizmet</th>
                  <th style="text-align: center;">Adet</th>
                  ${isInstallment ? `
                    <th style="text-align: center;">Ay</th>
                    <th style="text-align: right;">Aylık Taksit</th>
                  ` : hasDiscountedPrice ? `
                    <th style="text-align: right;">Birim Fiyat</th>
                    <th style="text-align: right;">İndirimli Fiyat</th>
                  ` : `
                    <th style="text-align: right;">Birim Fiyat</th>
                  `}
                  <th style="text-align: right;">Toplam</th>
                </tr>
              </thead>
              <tbody>
                ${offer.items.map(item => `
                  <tr>
                    <td style="font-weight: 500;">${item.product_name}</td>
                    <td style="text-align: center;">${item.quantity || 1}</td>
                    ${isInstallment ? `
                      <td style="text-align: center;">${item.months || 12}</td>
                      <td style="text-align: right; color: #2563EB; font-weight: 600;">${(item.monthly_payment || 0).toFixed(2)} ₺</td>
                    ` : hasDiscountedPrice ? `
                      <td style="text-align: right; color: #64748b;">${(item.unit_price || 0).toFixed(2)} ₺</td>
                      <td style="text-align: right; color: #2563EB; font-weight: 600;">${(item.discounted_price || item.unit_price || 0).toFixed(2)} ₺</td>
                    ` : `
                      <td style="text-align: right; color: #2563EB; font-weight: 600;">${(item.unit_price || 0).toFixed(2)} ₺</td>
                    `}
                    <td style="text-align: right; font-weight: 700; color: #0f172a;">${item.total.toFixed(2)} ₺</td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
            
            <!-- Summary -->
            <div class="summary-box">
              ${!isInstallment ? `
              <div class="summary-row">
                <span>Ara Toplam</span>
                <span>${offer.items.reduce((sum, item) => sum + item.total, 0).toFixed(2)} ₺</span>
              </div>
              <div class="summary-row">
                <span>KDV (%20)</span>
                <span>${(offer.items.reduce((sum, item) => sum + item.total, 0) * 0.20).toFixed(2)} ₺</span>
              </div>
              ` : ''}
              <div class="summary-row total-row">
                <span>Toplam</span>
                <span>${offer.total_amount.toFixed(2)} ₺</span>
              </div>
              
              <!-- Representative & Validity -->
              <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-top: 20px; padding-top: 20px; border-top: 2px solid #e2e8f0;">
                <div>
                  <div style="font-size: 11px; color: #64748b; font-weight: 600; margin-bottom: 4px;">Müşteri Yöneticisi</div>
                  <div style="font-size: 14px; color: #1e293b; font-weight: 600;">${offer.representative_full_name || offer.representative}</div>
                  ${offer.representative_phone ? `<div style="font-size: 12px; color: #64748b; margin-top: 2px;">${offer.representative_phone}</div>` : ''}
                </div>
                <div style="text-align: right;">
                  <div style="font-size: 11px; color: #64748b; font-weight: 600; margin-bottom: 4px;">Teklif Geçerlilik Tarihi</div>
                  <div style="font-size: 14px; color: #1e293b; font-weight: 600;">${offer.valid_until ? format(new Date(offer.valid_until), 'dd.MM.yyyy') : '-'}</div>
                </div>
              </div>
            </div>
            
            ${offer.notes && offer.notes.trim() !== '' ? `
            <!-- Notes -->
            <div style="background: white; border: 1px solid #e2e8f0; border-radius: 8px; padding: 20px; margin: 30px 0;">
              <div style="font-weight: 600; color: #475569; margin-bottom: 8px; font-size: 13px;">Not</div>
              <div style="color: #334155; line-height: 1.6; font-size: 13px;">${offer.notes}</div>
            </div>
            ` : ''}
          </div>
          
          <!-- Footer -->
          <div class="footer">
            <span>ProGo Mobil Sistemler Arvento Kurumsal İş Ortağıdır.</span>
            <img src="/arvento-logo.png" alt="Arvento" class="footer-logo">
          </div>
        </div>
      </body>
      </html>
    `;
  };

  const handleExportExcel = () => {
    // Filtre kontrolü - herhangi bir filtre uygulanmış mı?
    const today = new Date();
    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const defaultStartDate = format(firstDayOfMonth, 'yyyy-MM-dd');
    const defaultEndDate = format(today, 'yyyy-MM-dd');
    
    const isFiltered = searchQuery.trim() !== '' || 
                       startDate !== defaultStartDate || 
                       endDate !== defaultEndDate;
    
    // Filtre varsa filtrelenmiş veriyi, yoksa tüm veriyi kullan
    const dataToExport = isFiltered ? filteredOffers : offers;
    
    const exportData = dataToExport.map(offer => ({
      'Tarih': offer.created_at ? format(new Date(offer.created_at), 'dd.MM.yyyy HH:mm') : '-',
      'Teklif No': offer.offer_number || '',
      'Tür': offer.offer_type || 'Peşin',
      'Müşteri': offer.customer_name || '',
      'Başlık': offer.title || '',
      'Müşteri Yöneticisi': offer.representative_full_name || offer.representative || '',
      'Tutar (₺)': offer.total_amount || 0,
      'Geçerlilik Tarihi': offer.valid_until ? format(new Date(offer.valid_until), 'dd.MM.yyyy') : '-',
      'Notlar': offer.notes || ''
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Teklifler');
    
    const fileName = isFiltered 
      ? `teklifler_filtreli_${exportData.length}_adet.xlsx`
      : `tum_teklifler_${exportData.length}_adet.xlsx`;
    
    XLSX.writeFile(wb, fileName);
    toast.success(`${exportData.length} teklif Excel'e aktarıldı${isFiltered ? ' (filtrelenmiş)' : ' (tümü)'}`);
  };

  const resetForm = () => {
    // 5 gün sonrası için tarihi hesapla
    const fiveDaysLater = new Date();
    fiveDaysLater.setDate(fiveDaysLater.getDate() + 5);
    const defaultValidUntil = getLocalDateString(fiveDaysLater);
    
    setFormData({
      customer_id: '',
      offer_type: 'Peşin',
      title: '',
      items: [{ product_name: '', quantity: 1, unit_price: '', discount_percentage: 0, discounted_price: '', total: 0 }],
      notes: '',
      valid_until: defaultValidUntil,
      include_vat: true
    });
    setCustomerSearch('');
    setShowCustomerDropdown(false);
  };

  const handleNewOfferByType = (offerType) => {
    setEditingOffer(null);
    resetForm();
    // Teklif türüne göre başlık
    let title = 'Arvento Araç Takip Sistemleri Fiyat Teklifi';
    if (offerType === 'Arvento Taksitli') {
      title = 'Arvento Araç Takip Sistemleri Taksitli Fiyat Teklifi';
    } else if (offerType === 'Operatör Taksitli') {
      title = 'Operatör Taksitli Fiyat Teklifi';
    }
    setFormData(prev => ({
      ...prev,
      offer_type: offerType,
      title: title
    }));
    setShowDialog(true);
  };

  if (loading) {
    return <div className="text-center py-12">Yükleniyor...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        icon={FileText}
        title="Teklifler"
        subtitle="Müşteri tekliflerini yönetin"
      >
        <Button onClick={() => handleNewOfferByType('Peşin')} className="bg-white text-green-600 hover:bg-green-50 shadow-md">
          <Plus className="w-4 h-4 mr-2" />
          Peşin Teklif
        </Button>
        <Button onClick={() => handleNewOfferByType('Arvento Taksitli')} className="bg-white text-blue-600 hover:bg-blue-50 shadow-md">
          <Plus className="w-4 h-4 mr-2" />
          Arvento Taksitli
        </Button>
        <Button onClick={() => handleNewOfferByType('Operatör Taksitli')} className="bg-white text-orange-600 hover:bg-orange-50 shadow-md">
          <Plus className="w-4 h-4 mr-2" />
          Operatör Taksitli
        </Button>
        <Button onClick={handleExportExcel} className="bg-white text-purple-600 hover:bg-purple-50 shadow-md">
          <FileSpreadsheet className="w-4 h-4 mr-2" />
          Excel
        </Button>
      </PageHeader>

      {/* Filtreler - KOMPAKT */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 space-y-3">
        {/* Arama Kutusu */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <Input
            type="text"
            placeholder="Müşteri adı, teklif no veya başlık ile ara..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 text-sm"
          />
        </div>
        
        {/* Tarih Filtreleri */}
        <div className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-2">
          {/* Hızlı Filtre Butonları */}
          <Button
            onClick={() => {
              const today = new Date();
              const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
              const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
              const formatDate = (date) => {
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                return `${year}-${month}-${day}`;
              };
              setStartDate(formatDate(lastMonth));
              setEndDate(formatDate(lastMonthEnd));
            }}
            variant="outline"
            size="sm"
            className="text-xs h-8 px-2"
          >
            Geçen Ay
          </Button>
          <Button
            onClick={() => {
              const today = new Date();
              const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
              const formatDate = (date) => {
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                return `${year}-${month}-${day}`;
              };
              setStartDate(formatDate(firstDay));
              setEndDate(formatDate(today));
            }}
            variant="outline"
            size="sm"
            className="text-xs h-8 px-2"
          >
            Bu Ay
          </Button>
          
          {/* Tarih Seçiciler */}
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="px-2 py-1 text-xs border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          <span className="text-gray-400 text-xs">—</span>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="px-2 py-1 text-xs border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          
          {/* Temsilci Filtresi */}
          <select
            value={selectedRepresentative}
            onChange={(e) => setSelectedRepresentative(e.target.value)}
            className="px-2 py-1 text-xs border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
            data-testid="representative-filter"
          >
            <option value="all">Tüm Temsilciler</option>
            {representatives.map(rep => (
              <option key={rep.id} value={rep.username}>
                {rep.full_name || rep.username}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Offers List */}
      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full table-auto">
            <thead className="progo-gradient text-gray-800">
              <tr>
                <th className="px-4 py-3 text-left whitespace-nowrap">Tarih</th>
                <th className="px-4 py-3 text-left">Teklif No</th>
                <th className="px-4 py-3 text-left">Tür</th>
                <th className="px-4 py-3 text-left">Müşteri</th>
                <th className="px-4 py-3 text-left">Başlık</th>
                <th className="px-4 py-3 text-left">Müşteri Yöneticisi</th>
                <th className="px-4 py-3 text-right">Tutar</th>
                <th className="px-4 py-3 text-center">Geçerlilik</th>
                <th className="px-4 py-3 text-center">İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {filteredOffers.length === 0 ? (
                <tr>
                  <td colSpan="9" className="px-6 py-12 text-center text-gray-500">
                    <FileText className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                    <p>Seçili tarih aralığında teklif bulunamadı</p>
                  </td>
                </tr>
              ) : (
                filteredOffers.map((offer, index) => (
                  <tr 
                    key={offer.id} 
                    className="border-b hover:bg-gray-50 cursor-pointer" 
                    data-testid={`offer-row-${index}`}
                    onClick={() => navigate(`/customers/${offer.customer_id}`)}
                  >
                    <td className="px-4 py-3 text-sm whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        <Calendar className="w-3 h-3 text-gray-400" />
                        <span>{offer.created_at ? format(new Date(offer.created_at), 'dd MMM yyyy – HH:mm', { locale: tr }) : '-'}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 font-medium text-sm">
                      {offer.offer_number}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <span className={`px-2 py-1 rounded text-xs font-semibold ${
                        offer.offer_type === 'Peşin' ? 'bg-green-100 text-green-800' :
                        offer.offer_type === 'Arvento Taksitli' ? 'bg-blue-100 text-blue-800' :
                        'bg-orange-100 text-orange-800'
                      }`}>
                        {offer.offer_type || 'Peşin'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm font-medium">
                      {offer.customer_name}
                    </td>
                    <td className="px-4 py-3 text-sm">{offer.title}</td>
                    <td className="px-4 py-3 text-sm">
                      <div className="flex items-center space-x-2">
                        <User className="w-3 h-3 text-gray-400" />
                        <span>{offer.representative_full_name || offer.representative}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-right font-semibold text-green-600">
                      {offer.total_amount.toFixed(2)} ₺
                    </td>
                    <td className="px-4 py-3 text-sm text-center">
                      <div className="flex items-center justify-center space-x-1">
                        <Calendar className="w-3 h-3 text-gray-400" />
                        <span>{offer.valid_until ? format(new Date(offer.valid_until), 'dd MMM yyyy', { locale: tr }) : '-'}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-center space-x-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handlePreview(offer)}
                          title="Önizle"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDownloadPDF(offer)}
                          title="İndir"
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleEdit(offer)}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        {/* Silme butonu - admin, abonelik, temsilci için */}
                        {['admin', 'abonelik', 'temsilci'].includes(user?.role) && (
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => handleDelete(offer.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={(open) => {
        setShowDialog(open);
        if (open && !editingOffer) {
          // Yeni teklif için dialog açıldığında formu sıfırla
          resetForm();
        }
      }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingOffer ? 'Teklifi Düzenle' : 'Yeni Teklif Oluştur'} 
              <span className={`ml-2 px-2 py-1 rounded text-sm font-semibold ${
                formData.offer_type === 'Peşin' ? 'bg-green-100 text-green-800' :
                formData.offer_type === 'Arvento Taksitli' ? 'bg-blue-100 text-blue-800' :
                'bg-orange-100 text-orange-800'
              }`}>
                {formData.offer_type}
              </span>
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Müşteri Seçimi */}
            <div className="relative customer-search-container">
              <Label>Müşteri *</Label>
              <Input
                placeholder="Müşteri ara..."
                value={customerSearch}
                onChange={(e) => {
                  setCustomerSearch(e.target.value);
                  if (e.target.value.length > 0) {
                    setShowCustomerDropdown(true);
                  }
                }}
                onFocus={() => {
                  if (customerSearch.length > 0 && !formData.customer_id) {
                    setShowCustomerDropdown(true);
                  }
                }}
              />
              
              {/* Seçili Müşteri */}
              {formData.customer_id && !showCustomerDropdown && (
                <div className="mt-2 p-2 bg-blue-50 rounded border border-blue-200 flex justify-between items-center">
                  <span className="text-sm font-medium">
                    {customers.find(c => c.id === formData.customer_id)?.name}
                  </span>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setFormData({ ...formData, customer_id: '' });
                      setCustomerSearch('');
                      setShowCustomerDropdown(true);
                    }}
                  >
                    Değiştir
                  </Button>
                </div>
              )}
              
              {/* Dropdown */}
              {showCustomerDropdown && (
                <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  {(() => {
                    const query = customerSearch.toLowerCase();
                    const allEntities = [...customers, ...prospects];
                    // Önce baştan başlayanları, sonra kelime başlarında olanları göster
                    const startsWithResults = allEntities.filter(entity => 
                      entity.name.toLowerCase().startsWith(query) ||
                      entity.phone?.startsWith(customerSearch)
                    );
                    const wordStartResults = allEntities.filter(entity => 
                      !entity.name.toLowerCase().startsWith(query) &&
                      entity.name.toLowerCase().split(' ').some(word => word.startsWith(query))
                    );
                    return [...startsWithResults, ...wordStartResults].slice(0, 50);
                  })().map(customer => (
                      <div
                        key={customer.id}
                        className="p-3 hover:bg-gray-100 cursor-pointer border-b last:border-b-0"
                        onClick={() => {
                          setFormData({ ...formData, customer_id: customer.id });
                          setCustomerSearch(customer.name);
                          setShowCustomerDropdown(false);
                        }}
                      >
                        <div className="font-medium">{customer.name}</div>
                        {customer.email && (
                          <div className="text-xs text-gray-500">{customer.email}</div>
                        )}
                        {customer.phone && (
                          <div className="text-xs text-gray-500">{customer.phone}</div>
                        )}
                      </div>
                    ))}
                  {(() => {
                    const query = customerSearch.toLowerCase();
                    const startsWithResults = customers.filter(customer => 
                      customer.name.toLowerCase().startsWith(query) ||
                      customer.phone?.startsWith(customerSearch)
                    );
                    const wordStartResults = customers.filter(customer => 
                      !customer.name.toLowerCase().startsWith(query) &&
                      customer.name.toLowerCase().split(' ').some(word => word.startsWith(query))
                    );
                    return [...startsWithResults, ...wordStartResults].length === 0;
                  })() && (
                    <div className="p-3 text-center text-gray-500">
                      Müşteri bulunamadı
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Başlık */}
            <div>
              <Label>Teklif Başlığı *</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Örn: Araç Takip Sistemi Teklifi"
              />
            </div>

            {/* Geçerlilik Tarihi */}
            <div>
              <Label>Geçerlilik Tarihi *</Label>
              <Input
                type="date"
                value={formData.valid_until}
                onChange={(e) => setFormData({ ...formData, valid_until: e.target.value })}
              />
            </div>

            {/* Ürünler */}
            <div>
              <div className="flex justify-between items-center mb-2">
                <Label>Ürünler/Hizmetler *</Label>
                <Button type="button" size="sm" onClick={handleAddItem}>
                  <Plus className="w-4 h-4 mr-1" />
                  Ekle
                </Button>
              </div>
              
              <div className="space-y-3">
                {formData.items.map((item, index) => (
                  <div key={index} className="flex gap-2 items-end">
                    <div className="flex-1 relative">
                      <Label className="text-xs">Ürün/Hizmet</Label>
                      <Input
                        placeholder="Ürün/Hizmet adı (yazın veya seçin)"
                        value={item.product_name}
                        onChange={(e) => handleItemChange(index, 'product_name', e.target.value)}
                        onFocus={() => setShowProductDropdown(index)}
                      />
                      
                      {/* Ürün Dropdown - Tüm teklif türleri için */}
                      {showProductDropdown === index && products.length > 0 && (
                        <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                          {(() => {
                            const filteredProducts = products.filter(product => {
                              // for_offers kontrolü
                              if (product.for_offers !== true) return false;
                              
                              // İsim araması
                              if (!product.name.toLowerCase().includes(item.product_name.toLowerCase())) return false;
                              
                              // Teklif türüne göre kategori filtresi
                              if (formData.offer_type === 'Peşin') {
                                // Peşin için taksitli olmayanlar
                                return product.category !== 'Arvento Taksitli' && product.category !== 'Operatör Satışı';
                              } else if (formData.offer_type === 'Arvento Taksitli') {
                                // Sadece Arvento Taksitli kategorisi
                                return product.category === 'Arvento Taksitli';
                              } else if (formData.offer_type === 'Operatör Taksitli') {
                                // Sadece Operatör Satışı kategorisi
                                return product.category === 'Operatör Satışı';
                              }
                              return true;
                            });
                            
                            // Debug için
                            if (formData.offer_type === 'Operatör Taksitli' && filteredProducts.length === 0) {
                              console.log('Operatör Satışı ürün bulunamadı');
                              console.log('Tüm ürünler:', products.map(p => ({ name: p.name, category: p.category, for_offers: p.for_offers })));
                              console.log('Operatör Satışı kategorisindeki ürünler:', products.filter(p => p.category === 'Operatör Satışı'));
                            }
                            
                            const slicedProducts = filteredProducts.slice(0, 10);
                            
                            if (slicedProducts.length === 0) {
                              return [(
                                <div key="no-products" className="p-3 text-center text-gray-500">
                                  {formData.offer_type === 'Operatör Taksitli' 
                                    ? 'Operatör Satışı kategorisinde ürün bulunamadı. Lütfen önce ürün ekleyin.'
                                    : formData.offer_type === 'Arvento Taksitli'
                                    ? 'Arvento Taksitli kategorisinde ürün bulunamadı. Lütfen önce ürün ekleyin.'
                                    : 'Ürün bulunamadı'}
                                </div>
                              )];
                            }
                            
                            return slicedProducts;
                          })().map((product, idx) => (
                              product.id ? (
                                <div
                                  key={product.id}
                                  className="p-2 hover:bg-gray-100 cursor-pointer border-b last:border-b-0"
                                  onClick={() => {
                                    handleItemChange(index, 'product_name', product.name);
                                    if (formData.offer_type === 'Peşin') {
                                      handleItemChange(index, 'unit_price', product.sale_price || 0);
                                    } else {
                                      // Taksitli için aylık taksit tutarını getir
                                      handleItemChange(index, 'monthly_payment', product.sale_price || 0);
                                    }
                                    setShowProductDropdown(null);
                                  }}
                                >
                                  <div className="font-medium text-sm">{product.name}</div>
                                  <div className="text-xs text-gray-500">
                                    {formData.offer_type === 'Peşin' ? 'Satış Fiyatı' : 'Aylık Taksit'}: {product.sale_price?.toFixed(2)} ₺
                                  </div>
                                </div>
                              ) : product
                            ))}
                        </div>
                      )}
                    </div>
                    
                    {/* Peşin Teklif İçin */}
                    {formData.offer_type === 'Peşin' && (
                      <>
                        <div className="w-24">
                          <Label className="text-xs">Adet</Label>
                          <Input
                            type="number"
                            placeholder="1"
                            value={item.quantity}
                            onChange={(e) => {
                              const value = e.target.value === '' ? '' : parseFloat(e.target.value) || 0;
                              handleItemChange(index, 'quantity', value);
                            }}
                          />
                        </div>
                        <div className="w-32">
                          <Label className="text-xs">Birim Fiyat</Label>
                          <Input
                            type="number"
                            step="0.01"
                            placeholder="0.00"
                            value={item.unit_price || ''}
                            onChange={(e) => {
                              const value = e.target.value;
                              handleItemChange(index, 'unit_price', value);
                            }}
                          />
                        </div>
                        <div className="w-32">
                          <Label className="text-xs">İndirimli Fiyat</Label>
                          <Input
                            type="number"
                            step="0.01"
                            placeholder="İsteğe bağlı"
                            value={item.discounted_price || ''}
                            onChange={(e) => {
                              const value = e.target.value;
                              handleItemChange(index, 'discounted_price', value);
                            }}
                          />
                        </div>
                        <div className="w-32">
                          <Label className="text-xs">Toplam</Label>
                          <Input
                            value={item.total.toFixed(2)}
                            readOnly
                            className="bg-gray-50"
                          />
                        </div>
                      </>
                    )}
                    
                    {/* Taksitli Teklifler İçin */}
                    {(formData.offer_type === 'Arvento Taksitli' || formData.offer_type === 'Operatör Taksitli') && (
                      <>
                        <div className="w-24">
                          <Label className="text-xs">Adet</Label>
                          <Input
                            type="number"
                            placeholder="1"
                            value={item.quantity || 1}
                            onChange={(e) => {
                              const value = e.target.value === '' ? 1 : parseFloat(e.target.value) || 1;
                              handleItemChange(index, 'quantity', value);
                            }}
                          />
                        </div>
                        <div className="w-32">
                          <Label className="text-xs">Ay</Label>
                          <Select
                            value={item.months?.toString() || '12'}
                            onValueChange={(value) => handleItemChange(index, 'months', parseInt(value))}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Ay Seçin" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="12">12 Ay</SelectItem>
                              <SelectItem value="24">24 Ay</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="w-32">
                          <Label className="text-xs">Aylık Taksit</Label>
                          <Input
                            type="number"
                            step="0.01"
                            placeholder="0.00"
                            value={item.monthly_payment || ''}
                            onChange={(e) => {
                              const value = e.target.value === '' ? '' : parseFloat(e.target.value) || 0;
                              handleItemChange(index, 'monthly_payment', value);
                            }}
                          />
                        </div>
                        <div className="w-32">
                          <Label className="text-xs">Toplam</Label>
                          <Input
                            value={item.total.toFixed(2)}
                            readOnly
                            className="bg-gray-50"
                          />
                        </div>
                      </>
                    )}
                    {formData.items.length > 1 && (
                      <Button
                        type="button"
                        size="sm"
                        variant="destructive"
                        onClick={() => handleRemoveItem(index)}
                        className="mt-5"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>

              {/* Toplam */}
              <div className="mt-4 space-y-2">
                {/* Ara Toplam */}
                {formData.offer_type !== 'Arvento Taksitli' && formData.offer_type !== 'Operatör Taksitli' && (
                  <div className="p-3 bg-gray-50 rounded flex justify-between items-center">
                    <span className="font-medium">Ara Toplam:</span>
                    <span className="text-lg font-semibold flex items-center gap-1">
                      {formData.items.reduce((sum, item) => sum + item.total, 0).toFixed(2)} ₺
                    </span>
                  </div>
                )}
                
                {/* KDV - Sadece Arvento Taksitli ve Operatör Taksitli DIŞINDA */}
                {formData.offer_type !== 'Arvento Taksitli' && formData.offer_type !== 'Operatör Taksitli' && (
                  <div className="p-3 bg-gray-50 rounded flex justify-between items-center">
                    <span className="font-medium">KDV (%20):</span>
                    <span className="text-lg font-semibold text-orange-600 flex items-center gap-1">
                      {(formData.items.reduce((sum, item) => sum + item.total, 0) * 0.20).toFixed(2)} ₺
                    </span>
                  </div>
                )}
                
                {/* Genel Toplam */}
                <div className="p-4 bg-green-50 rounded flex justify-between items-center border-2 border-green-200">
                  <span className="font-bold text-lg">Genel Toplam:</span>
                  <span className="text-2xl font-bold text-green-600 flex items-center gap-1">
                    {formData.offer_type === 'Arvento Taksitli' || formData.offer_type === 'Operatör Taksitli'
                      ? formData.items.reduce((sum, item) => sum + item.total, 0).toFixed(2)
                      : (formData.items.reduce((sum, item) => sum + item.total, 0) * 1.20).toFixed(2)
                    } ₺
                  </span>
                </div>
              </div>
            </div>

            {/* Notlar */}
            <div>
              <Label>Notlar / Açıklamalar</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Teklif ile ilgili ek bilgiler..."
                rows={4}
                className="bg-white border-gray-300 focus:border-blue-500 focus:ring-blue-500"
              />
            </div>

            {/* Buttons */}
            <div className="flex justify-end space-x-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                İptal
              </Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white">
                {editingOffer ? 'Güncelle' : 'Oluştur'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Teklif Önizleme</DialogTitle>
          </DialogHeader>
          
          {previewOffer && (
            <div dangerouslySetInnerHTML={{ __html: generateOfferHTML(previewOffer) }} />
          )}
          
          <div className="flex justify-end space-x-2 pt-4">
            <Button variant="outline" onClick={() => setShowPreview(false)}>
              Kapat
            </Button>
            <Button onClick={() => handleDownloadPDF(previewOffer)} className="bg-blue-600 hover:bg-blue-700 text-white">
              <Download className="w-4 h-4 mr-2" />
              İndir
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Offers;
